import React, { useState } from 'react';
import { RefreshCw, CalendarClock, Archive, Trash2 } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import type { Activity } from '../../../../state/api/organisationEngineSlice';

type ActivityVersionContentProps = {
    activity: Activity;
    onChange: () => void;
    onUpdateValidity: (activityId: string, validFrom: string, validTo: string | null) => Promise<void>;
    onArchive: () => Promise<void>;
    onRemove: () => Promise<void>;
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className='flex py-1.5'>
        <span className='text-xs font-medium text-grey-70 w-28 shrink-0'>{label}</span>
        <span className='text-xs'>{value}</span>
    </div>
);

const ActivityVersionContent = ({ activity, onChange, onUpdateValidity, onArchive, onRemove }: ActivityVersionContentProps) => {
    const codeDisplay = activity.isic_description ? `${activity.isic_code} — ${activity.isic_description}` : activity.isic_code;

    const [editingValidity, setEditingValidity] = useState(false);
    const [validFrom, setValidFrom] = useState(activity.valid_from.split('T')[0]);
    const [stillActive, setStillActive] = useState(activity.valid_to === null);
    const [validTo, setValidTo] = useState(activity.valid_to?.split('T')[0] ?? '');
    const [isSaving, setIsSaving] = useState(false);
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const handleSaveValidity = async () => {
        if (!onUpdateValidity) return;
        setIsSaving(true);
        try {
            await onUpdateValidity(
                activity.id,
                new Date(validFrom).toISOString(),
                stillActive ? null : validTo ? new Date(validTo).toISOString() : null
            );
            setEditingValidity(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleArchive = async () => {
        setIsArchiving(true);
        try {
            await onArchive();
            setShowArchiveDialog(false);
        } finally {
            setIsArchiving(false);
        }
    };

    const handleRemove = async () => {
        setIsRemoving(true);
        try {
            await onRemove();
            setShowRemoveDialog(false);
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col border rounded-md overflow-hidden'>
                <div className='bg-grey-10 px-3 py-2 text-xs font-semibold text-grey-70'>Details</div>
                <div className='px-3 py-2'>
                    <InfoRow label='ID' value={activity.id} />
                    <InfoRow label='ISIC Code' value={codeDisplay} />
                    {editingValidity ? (
                        <>
                            <div className='flex flex-col gap-1 py-1.5'>
                                <label className='text-xs font-medium text-grey-70'>Valid from</label>
                                <input
                                    type='date'
                                    className='text-xs border rounded px-2 py-1'
                                    value={validFrom}
                                    onChange={e => setValidFrom(e.target.value)}
                                    required
                                />
                            </div>
                            <div className='flex flex-col gap-1 py-1.5'>
                                <label className='flex items-center gap-2 cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={stillActive}
                                        onChange={e => setStillActive(e.target.checked)}
                                        className='w-3.5 h-3.5 accent-primary'
                                        data-testid='act-still-active-checkbox'
                                    />
                                    <span className='text-xs font-medium text-grey-70'>Still active (no end date)</span>
                                </label>
                                {!stillActive && (
                                    <input
                                        type='date'
                                        className='text-xs border rounded px-2 py-1 mt-1'
                                        value={validTo}
                                        onChange={e => setValidTo(e.target.value)}
                                        required
                                    />
                                )}
                            </div>
                            <div className='flex gap-2 pt-2'>
                                <PrimaryButton onClick={handleSaveValidity} disabled={isSaving} data-testid='act-validity-save'>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </PrimaryButton>
                                <SecondaryButton onClick={() => setEditingValidity(false)}>Cancel</SecondaryButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <InfoRow label='Valid from' value={new Date(activity.valid_from).toLocaleDateString()} />
                            <InfoRow
                                label='Valid to'
                                value={activity.valid_to ? new Date(activity.valid_to).toLocaleDateString() : 'Ongoing'}
                            />
                        </>
                    )}
                    <InfoRow label='Created' value={new Date(activity.created_at).toLocaleDateString()} />
                </div>
            </div>
            <div className='flex gap-2 flex-wrap'>
                <SecondaryButton onClick={onChange} data-testid='version-change-button'>
                    <RefreshCw className='w-4 h-4' /> Change
                </SecondaryButton>
                {!editingValidity && (
                    <SecondaryButton onClick={() => setEditingValidity(true)} data-testid='version-edit-validity-button'>
                        <CalendarClock className='w-4 h-4' /> Edit Validity
                    </SecondaryButton>
                )}
                {!activity.valid_to && (
                    <SecondaryButton onClick={() => setShowArchiveDialog(true)} data-testid='version-archive-button'>
                        <Archive className='w-4 h-4' /> Archive
                    </SecondaryButton>
                )}
                <SecondaryButton onClick={() => setShowRemoveDialog(true)} data-testid='version-remove-button'>
                    <Trash2 className='w-4 h-4' /> Remove
                </SecondaryButton>
            </div>

            <ControlledConfirmDialog
                open={showArchiveDialog}
                onOpenChange={setShowArchiveDialog}
                variant='confirm'
                title='Archive Activity'
                description='Are you sure you want to archive this activity? This will close its validity period.'
                confirmLabel='Archive'
                onConfirm={handleArchive}
                isConfirming={isArchiving}
            />

            <ControlledConfirmDialog
                open={showRemoveDialog}
                onOpenChange={setShowRemoveDialog}
                variant='confirm'
                title='Remove Activity'
                description='Are you sure you want to remove this activity? This cannot be undone.'
                confirmLabel='Remove'
                onConfirm={handleRemove}
                isConfirming={isRemoving}
            />
        </div>
    );
};

export default ActivityVersionContent;
